import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Gift, Heart, Package, Percent, Star, User, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import useAuthStore from '../store/authStore';
import axiosInstance from '../api/axios';
import { toast } from 'sonner';

const AccountPage = () => {
  const navigate = useNavigate();
  const { user, setAuth } = useAuthStore();
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Login form
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  
  // Register form
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: ''
  });

  // If user is logged in, redirect to profile
  React.useEffect(() => {
    if (user) {
      navigate('/profile');
    }
  }, [user, navigate]);

  const memberBenefits = [
    { 
      icon: Gift, 
      color: 'text-pink-500',
      bgColor: 'bg-pink-100',
      title: '10% Willkommens-Rabatt', 
      desc: 'Erhalten Sie sofort 10% auf Ihre erste Bestellung' 
    },
    { 
      icon: Percent, 
      color: 'text-purple-500',
      bgColor: 'bg-purple-100',
      title: 'Exklusive Coupons', 
      desc: 'Regelmäßige Rabattaktionen nur für Mitglieder' 
    },
    { 
      icon: Heart, 
      color: 'text-red-500',
      bgColor: 'bg-red-100',
      title: 'Wunschliste', 
      desc: 'Speichern Sie Ihre Lieblingsprodukte' 
    },
    { 
      icon: Package, 
      color: 'text-blue-500',
      bgColor: 'bg-blue-100',
      title: 'Bestellübersicht', 
      desc: 'Alle Bestellungen immer im Blick' 
    },
    { 
      icon: Star, 
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-100',
      title: 'Bonus-Coupons', 
      desc: '4 Willkommens-Coupons bei der Registrierung' 
    },
  ];

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axiosInstance.post('/auth/login', loginData);
      const { user, token } = response.data;
      
      setAuth(user, token);
      toast.success('Erfolgreich angemeldet!');
      
      // Navigate based on role
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/profile');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    if (registerData.password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen haben');
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post('/auth/register', {
        email: registerData.email,
        password: registerData.password,
        first_name: registerData.first_name,
        last_name: registerData.last_name
      });
      
      const { user, token } = response.data;
      setAuth(user, token);
      
      toast.success('Willkommen! Ihr Konto wurde erstellt.');
      navigate('/profile');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F0EB' }}>
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Mein Konto</h1>
            <p className="text-sm text-gray-500">Anmelden oder Registrieren</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 pb-24">
        {/* Benefits Section */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-500" />
            Ihre Vorteile als Mitglied
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {memberBenefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <Card key={idx} className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${benefit.bgColor}`}>
                        <Icon className={`h-5 w-5 ${benefit.color}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm text-gray-900">{benefit.title}</h3>
                        <p className="text-xs text-gray-500">{benefit.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Login/Register Tabs */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-0">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full grid grid-cols-2 rounded-none rounded-t-lg h-14">
                <TabsTrigger 
                  value="login" 
                  className="text-base data-[state=active]:bg-[#8B7355] data-[state=active]:text-white rounded-none rounded-tl-lg"
                >
                  Anmelden
                </TabsTrigger>
                <TabsTrigger 
                  value="register"
                  className="text-base data-[state=active]:bg-[#8B7355] data-[state=active]:text-white rounded-none rounded-tr-lg"
                >
                  Registrieren
                </TabsTrigger>
              </TabsList>

              {/* Login Tab */}
              <TabsContent value="login" className="p-6 mt-0">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">E-Mail</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="ihre@email.de"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="login-password">Passwort</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        className="pl-10 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Link to="/forgot-password" className="text-sm text-[#8B7355] hover:underline">
                      Passwort vergessen?
                    </Link>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#8B7355] hover:bg-[#6D5A47] text-white h-12"
                    disabled={loading}
                  >
                    {loading ? 'Wird angemeldet...' : 'Anmelden'}
                  </Button>
                </form>
              </TabsContent>

              {/* Register Tab */}
              <TabsContent value="register" className="p-6 mt-0">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">Vorname</Label>
                      <Input
                        id="first_name"
                        placeholder="Max"
                        value={registerData.first_name}
                        onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Nachname</Label>
                      <Input
                        id="last_name"
                        placeholder="Mustermann"
                        value={registerData.last_name}
                        onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-email">E-Mail</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="ihre@email.de"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-password">Passwort</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="register-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Mindestens 6 Zeichen"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        className="pl-10 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Passwort bestätigen</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="confirm-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Passwort wiederholen"
                        value={registerData.confirmPassword}
                        onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800">
                      <strong>🎁 Willkommensgeschenk:</strong> Registrieren Sie sich jetzt und erhalten Sie 4 exklusive Coupons!
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#8B7355] hover:bg-[#6D5A47] text-white h-12"
                    disabled={loading}
                  >
                    {loading ? 'Wird registriert...' : 'Kostenlos registrieren'}
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    Mit der Registrierung akzeptieren Sie unsere{' '}
                    <Link to="/agb" className="text-[#8B7355] hover:underline">AGB</Link>
                    {' '}und{' '}
                    <Link to="/datenschutz" className="text-[#8B7355] hover:underline">Datenschutzerklärung</Link>.
                  </p>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Continue as Guest */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600 mb-3">Noch nicht überzeugt?</p>
          <Button 
            variant="outline" 
            onClick={() => navigate('/products')}
            className="border-[#8B7355] text-[#8B7355]"
          >
            Weiter als Gast stöbern
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
